import React, { useState, useEffect } from 'react';
import Preloader from '../../components/common/Preloader';
import { getTimeChangeRequests, handleTimeChangeRequest } from '../../services/adminService';
import { formatDate } from '../../utils/format';
import './AttendanceManagement.css';

const getTimeString = (dateStr, defaultTime) => {
    if (!dateStr) return defaultTime;
    const d = new Date(dateStr);
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

const combineDateAndTimeStr = (dateVal, timeStr) => {
    if (!timeStr) return null;
    const d = new Date(dateVal);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T${timeStr}`;
};

const formatTime12h = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
};

const AttendanceManagement = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [processing, setProcessing] = useState(null);
    const [editRequest, setEditRequest] = useState(null);
    const [editData, setEditData] = useState({ loginTime: '', logoutTime: '' });
    const [activeTab, setActiveTab] = useState('Pending');

    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const data = await getTimeChangeRequests();
            setRequests(data);
        } catch (err) {
            setError('Failed to fetch requests');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async (id, action, customData = null) => {
        try {
            setProcessing(id);
            await handleTimeChangeRequest(id, { action, ...customData });
            setEditRequest(null);
            fetchRequests();
        } catch (err) {
            setError(err.message || 'Action failed');
        } finally {
            setProcessing(null);
        }
    };

    const openEditModal = (request) => {
        setEditRequest(request);
        setEditData({
            loginTime: getTimeString(request.changeRequest.requestedLogin, '10:00'),
            logoutTime: getTimeString(request.changeRequest.requestedLogout, '19:00')
        });
    };

    if (loading) return <Preloader />;

    const pendingCount = requests.filter(r => r.changeRequest?.status === 'pending').length;
    const approvedCount = requests.filter(r => r.changeRequest?.status === 'approved').length;
    const rejectedCount = requests.filter(r => r.changeRequest?.status === 'rejected').length;

    const filteredRequests = requests.filter(req => {
        if (activeTab === 'All') return true;
        if (activeTab === 'Pending') return req.changeRequest?.status === 'pending';
        if (activeTab === 'Approved') return req.changeRequest?.status === 'approved';
        if (activeTab === 'Rejected') return req.changeRequest?.status === 'rejected';
        return true;
    });

    return (
        <div className="attendance-management">
            <h1>Attendance & Time Change Requests</h1>

            {error && <div className="error-message">{error}</div>}

            <div className="filter-tabs">
                <button className={`filter-tab ${activeTab === 'All' ? 'active' : ''}`} onClick={() => setActiveTab('All')}>
                    All <span className="badge">{requests.length}</span>
                </button>
                <button className={`filter-tab ${activeTab === 'Pending' ? 'active' : ''}`} onClick={() => setActiveTab('Pending')}>
                    Pending <span className="badge">{pendingCount}</span>
                </button>
                <button className={`filter-tab ${activeTab === 'Approved' ? 'active' : ''}`} onClick={() => setActiveTab('Approved')}>
                    Approved <span className="badge">{approvedCount}</span>
                </button>
                <button className={`filter-tab ${activeTab === 'Rejected' ? 'active' : ''}`} onClick={() => setActiveTab('Rejected')}>
                    Rejected <span className="badge">{rejectedCount}</span>
                </button>
            </div>

            <div className="requests-container">
                {filteredRequests.length === 0 ? (
                    <div className="no-requests">No requests found for this filter.</div>
                ) : (
                    <div className="requests-grid">
                        {filteredRequests.map(request => (
                            <div key={request._id} className="request-card">
                                <div className="request-header">
                                    <div className="employee-info">
                                        <span className="employee-name">{request.employeeId.name}</span>
                                        <span className="employee-email">{request.employeeId.email}</span>
                                    </div>
                                    <span className="request-date">{formatDate(request.date)}</span>
                                </div>
                                <div className="request-body">
                                    <div className="time-comparison">
                                        <div className="time-block original">
                                            <label>Original</label>
                                            <p>In: {formatTime12h(request.changeRequest?.originalLogin || request.loginTime)}</p>
                                            <p>Out: {formatTime12h(request.changeRequest?.originalLogout || request.logoutTime)}</p>
                                        </div>
                                        <div className="time-block arrow">➔</div>
                                        <div className="time-block requested">
                                            <label>{request.changeRequest?.status === 'approved' ? 'Approved' : 'Requested'}</label>
                                            <p>In: {formatTime12h(request.changeRequest?.status === 'approved' ? request.loginTime : request.changeRequest?.requestedLogin)}</p>
                                            <p>Out: {formatTime12h(request.changeRequest?.status === 'approved' ? request.logoutTime : request.changeRequest?.requestedLogout)}</p>
                                        </div>
                                    </div>
                                    <div className="request-reason">
                                        <label>Reason:</label>
                                        <p>{request.changeRequest.reason}</p>
                                    </div>
                                </div>
                                <div className="request-footer">
                                    {(!request.changeRequest?.status || request.changeRequest?.status === 'pending') ? (
                                        <>
                                            <button
                                                className="btn-approve"
                                                disabled={processing === request._id}
                                                onClick={() => openEditModal(request)}
                                            >
                                                Review & Approve
                                            </button>
                                            <button
                                                className="btn-reject"
                                                disabled={processing === request._id}
                                                onClick={() => handleAction(request._id, 'rejected')}
                                            >
                                                Reject
                                            </button>
                                        </>
                                    ) : (
                                        <div className={`status-label status-${request.changeRequest?.status}`}>
                                            {request.changeRequest?.status.toUpperCase()}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Edit / Approve Modal */}
            {editRequest && (
                <div className="modal-overlay">
                    <div className="modal-content attendance-edit-modal">
                        <h3>Approve Time Change</h3>
                        <p style={{ margin: '0 0 1rem 0' }}>Employee: <strong>{editRequest.employeeId.name}</strong></p>
                        
                        <div className="request-modal-date" style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: '500', color: '#374151' }}>
                          Date: <strong>{formatDate(editRequest.date)}</strong>
                        </div>
                        
                        {/* Original Times Summary */}
                        <div className="original-times-summary" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', padding: '0.75rem', backgroundColor: '#f3f4f6', borderRadius: '6px', marginBottom: '1rem' }}>
                          <div>
                            <span style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 'bold' }}>Original In</span>
                            <span style={{ fontSize: '0.95rem', fontWeight: '600', color: '#1f2937' }}>{formatTime12h(editRequest.changeRequest?.originalLogin || editRequest.loginTime)}</span>
                          </div>
                          <div>
                            <span style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 'bold' }}>Original Out</span>
                            <span style={{ fontSize: '0.95rem', fontWeight: '600', color: '#1f2937' }}>{formatTime12h(editRequest.changeRequest?.originalLogout || editRequest.logoutTime)}</span>
                          </div>
                        </div>

                        {/* Requested Times Summary */}
                        <div className="request-details-box" style={{ padding: '0.75rem', borderLeft: '4px solid #6366f1', backgroundColor: '#f0f2fe', borderRadius: '4px', marginBottom: '1.25rem' }}>
                            <p style={{ margin: '0 0 0.5rem 0' }}><strong>Employee Reason:</strong> {editRequest.changeRequest.reason}</p>
                            <p style={{ margin: 0 }}><strong>Requested:</strong> In: {formatTime12h(editRequest.changeRequest.requestedLogin)} | Out: {formatTime12h(editRequest.changeRequest.requestedLogout)}</p>
                        </div>

                        <form onSubmit={(e) => {
                            e.preventDefault();
                            const payload = {
                                loginTime: combineDateAndTimeStr(editRequest.date, editData.loginTime),
                                logoutTime: combineDateAndTimeStr(editRequest.date, editData.logoutTime)
                            };
                            handleAction(editRequest._id, 'approved', payload);
                        }}>
                            <div className="form-group">
                                <label>Confirm Login Time:</label>
                                <input
                                    type="time"
                                    value={editData.loginTime}
                                    onChange={(e) => setEditData({ ...editData, loginTime: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Confirm Logout Time:</label>
                                <input
                                    type="time"
                                    value={editData.logoutTime}
                                    onChange={(e) => setEditData({ ...editData, logoutTime: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="modal-actions">
                                <button type="submit" className="btn-save" disabled={processing === editRequest._id}>
                                    {processing === editRequest._id ? 'Saving...' : 'Confirm & Approve'}
                                </button>
                                <button type="button" className="btn-cancel" onClick={() => setEditRequest(null)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AttendanceManagement;
